import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuctionHouse } from '../contexts/AuctionHouseContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { format } from 'date-fns';

interface LeaderboardEntry {
  userId: string;
  displayName: string;
  ringerScore: number;
  totalPredictions: number;
  netProfit: number;
  rank: number;
}

const Home: React.FC = () => {
  const { currentUser } = useAuth();
  const { currentAuctionHouse, isAggregator, loading: houseLoading } = useAuctionHouse();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [topRingers, setTopRingers] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Use context values
  const auctionHouseName = currentAuctionHouse?.name || "AuctionRingers";
  const auctionHouseId = currentAuctionHouse?.id || "schmidt-auctions";

  useEffect(() => {
    if (!houseLoading) {
      loadAuctions();
      loadTopRingers();
    }
  }, [houseLoading, auctionHouseId]);

  const loadAuctions = async () => {
    try {
      const auctionsRef = collection(db, 'auctions');
      const q = query(
        auctionsRef,
        where('auctionHouseId', '==', auctionHouseId),
        where('status', 'in', ['scheduled', 'live']),
        orderBy('startDate', 'asc'),
        limit(6)
      );
      
      const snapshot = await getDocs(q);
      const auctionsList: Auction[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        auctionsList.push({
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction);
      });

      setAuctions(auctionsList);
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopRingers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userStatsSnapshot = await getDocs(collection(db, 'userStats'));
      const predictionsSnapshot = await getDocs(collection(db, 'predictions'));

      const statsMap = new Map();
      userStatsSnapshot.forEach(doc => {
        statsMap.set(doc.id, doc.data());
      });

      const predictionStats = new Map<string, { totalWagered: number; totalEarned: number; netProfit: number; totalPredictions: number }>();

      predictionsSnapshot.forEach(doc => {
        const prediction = doc.data();
        const userId = prediction.userId;
        
        if (!predictionStats.has(userId)) {
          predictionStats.set(userId, {
            totalWagered: 0,
            totalEarned: 0,
            netProfit: 0,
            totalPredictions: 0
          });
        }

        const stats = predictionStats.get(userId)!;
        stats.totalPredictions++;
        stats.totalWagered += prediction.wagerAmount || 0;

        if (prediction.status === 'won') {
          stats.totalEarned += prediction.ringersEarned || 0;
          stats.netProfit += (prediction.ringersEarned || 0) - (prediction.wagerAmount || 0);
        } else if (prediction.status === 'lost') {
          stats.netProfit -= prediction.wagerAmount || 0;
        }
      });

      const entries: LeaderboardEntry[] = [];
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        const userId = doc.id;
        
        if (userData.role !== 'ringer') return;

        const stats = statsMap.get(userId) || {};
        const predStats = predictionStats.get(userId) || {
          totalWagered: 0,
          totalEarned: 0,
          netProfit: 0,
          totalPredictions: 0
        };

        entries.push({
          userId,
          displayName: userData.displayName || 'Anonymous',
          ringerScore: stats.ringerScore || 0,
          totalPredictions: predStats.totalPredictions,
          netProfit: predStats.netProfit,
          rank: 0
        });
      });

      entries.sort((a, b) => b.ringerScore - a.ringerScore);
      entries.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      setTopRingers(entries.slice(0, 10));
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  // Show loading state while auction house loads
  if (houseLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-purple-700 text-white rounded-2xl p-8 md:p-12">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {currentAuctionHouse ? (
              `Welcome to ${currentAuctionHouse.name}`
            ) : (
              'Welcome to AuctionRingers'
            )}
          </h1>
          <p className="text-xl md:text-2xl mb-6 text-primary-100">
            Predict. Wager. Win.
          </p>
          <p className="text-lg mb-8 text-primary-50">
            {currentAuctionHouse ? (
              `Make predictions on our upcoming auctions using Ringer$. The closer you predict, the more you win! Optionally place left bids to secure items you really want.`
            ) : (
              'Make predictions on auctions from all participating auction houses. The closer you predict, the more you win!'
            )}
          </p>
          {!currentUser ? (
            <div className="flex flex-wrap gap-4">
              <Link to="/register" className="bg-white text-primary-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-primary-50 transition-colors">
                Get Started Free
              </Link>
              <Link to="/how-it-works" className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-colors">
                How It Works
              </Link>
            </div>
          ) : (
            <Link to="/auctions" className="bg-white text-primary-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-primary-50 transition-colors inline-block">
              View Auctions
            </Link>
          )}
        </div>
      </div>

      {/* Portal Info Badge */}
      {currentAuctionHouse && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏛️</span>
            <div>
              <p className="font-semibold text-blue-900">
                You're viewing {currentAuctionHouse.name}
              </p>
              <p className="text-sm text-blue-700">
                {currentAuctionHouse.tagline || currentAuctionHouse.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Auctions */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold">
            {isAggregator ? 'Featured Auctions' : 'Current & Upcoming Auctions'}
          </h2>
          <Link to="/auctions" className="text-primary-600 hover:text-primary-700 font-semibold">
            View All →
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading auctions...</p>
          </div>
        ) : auctions.length === 0 ? (
          <div className="card text-center py-12">
            <p className="text-gray-600 text-lg mb-4">No auctions scheduled yet</p>
            <p className="text-gray-500">Check back soon for upcoming auctions!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auctions.map((auction) => (
              <div key={auction.id} className="card hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-xl mb-2">{auction.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {auction.description}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-2 ${
                    auction.status === 'live' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {auction.status === 'live' ? '🔴 LIVE' : 'Upcoming'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-semibold mr-2">📦 Lots:</span>
                    <span>{auction.totalLots}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-semibold mr-2">📅 Date:</span>
                    <span>{format(new Date(auction.startDate), 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-semibold mr-2">⏰ Time:</span>
                    <span>{format(new Date(auction.startDate), 'h:mm a')}</span>
                  </div>
                  {auction.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-semibold mr-2">📍 Location:</span>
                      <span>{auction.location}</span>
                    </div>
                  )}
                </div>

                <Link
                  to={`/auctions/${auction.id}`}
                  className="btn-primary w-full text-center"
                >
                  {auction.status === 'live' ? 'Predict Now' : 'View Auction'}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Two Column Layout - Leaderboard & Redemption */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Top Ringers Leaderboard */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">🏆 Top Ringers</h2>
            <Link to="/leaderboard" className="text-primary-600 hover:text-primary-700 font-semibold text-sm">
              View All →
            </Link>
          </div>

          {topRingers.length === 0 ? (
            <p className="text-center text-gray-600 py-8">
              No predictions yet. Be the first!
            </p>
          ) : (
            <div className="space-y-3">
              {topRingers.map((ringer) => (
                <div 
                  key={ringer.userId}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    ringer.rank <= 3 ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold w-8">
                      {getRankBadge(ringer.rank)}
                    </span>
                    <div>
                      <p className="font-semibold">{ringer.displayName}</p>
                      <p className="text-xs text-gray-600">
                        {ringer.totalPredictions} prediction{ringer.totalPredictions !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary-600">{ringer.ringerScore}</p>
                    <p className={`text-xs ${ringer.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {ringer.netProfit >= 0 ? '+' : ''}{ringer.netProfit} R$
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Redemption Store */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">🎁 Redemption Store</h2>
            <Link to="/redemption" className="text-primary-600 hover:text-primary-700 font-semibold text-sm">
              Browse All →
            </Link>
          </div>

          <div className="space-y-4">
            {/* Featured Items - Placeholder */}
            <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">🎁</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Gift Card - $25 Value</h3>
                  <p className="text-sm text-gray-600 mb-2">Redeem for merchandise or auction credits</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-primary-600">500 R$</span>
                    <button className="btn-secondary text-sm py-1 px-3" disabled>
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">🏆</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Premium Item Voucher</h3>
                  <p className="text-sm text-gray-600 mb-2">First pick on select auction items</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-primary-600">2,500 R$</span>
                    <button className="btn-secondary text-sm py-1 px-3" disabled>
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">📦</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Mystery Box</h3>
                  <p className="text-sm text-gray-600 mb-2">Random item from past auctions</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-primary-600">1,000 R$</span>
                    <button className="btn-secondary text-sm py-1 px-3" disabled>
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-sm text-blue-800">
                💡 Earn Ringer$ by making accurate predictions and redeem for real items!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works - Quick Overview */}
      <div className="card bg-gradient-to-br from-gray-50 to-gray-100">
        <h2 className="text-2xl font-bold mb-6 text-center">How AuctionRinger Works</h2>
        
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              1️⃣
            </div>
            <h3 className="font-bold mb-2">Browse Auctions</h3>
            <p className="text-sm text-gray-600">
              View upcoming {currentAuctionHouse ? `${currentAuctionHouse.name} auctions` : 'auctions from all houses'} and lot details
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              2️⃣
            </div>
            <h3 className="font-bold mb-2">Make Predictions</h3>
            <p className="text-sm text-gray-600">
              Wager Ringer$ on what you think items will sell for
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              3️⃣
            </div>
            <h3 className="font-bold mb-2">Watch & Win</h3>
            <p className="text-sm text-gray-600">
              Follow the live auction and see if your predictions hit
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              4️⃣
            </div>
            <h3 className="font-bold mb-2">Earn Rewards</h3>
            <p className="text-sm text-gray-600">
              Win multiplied Ringer$ and redeem for real items
            </p>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link to="/how-it-works" className="btn-primary inline-block">
            Learn More About AuctionRinger
          </Link>
        </div>
      </div>

      {/* Call to Action */}
      {!currentUser && (
        <div className="card bg-gradient-to-r from-primary-600 to-purple-700 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Predicting?</h2>
          <p className="text-xl mb-6 text-primary-50">
            {isAggregator ? (
              'Join AuctionRinger and start earning Ringer$ today!'
            ) : (
              `Join ${auctionHouseName} on AuctionRinger and start earning Ringer$ today!`
            )}
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/register" className="bg-white text-primary-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-primary-50 transition-colors">
              Sign Up Free
            </Link>
            <Link to="/login" className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-colors">
              Log In
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;